/**
 * CTFHud — DOM overlay for Capture the Flag mode.
 * Manages score, timer, flag status indicators, and notifications.
 * All elements are created programmatically; no HTML required.
 */
export class CTFHud {
  private root!: HTMLElement;
  private redScoreEl!: HTMLElement;
  private blueScoreEl!: HTMLElement;
  private timerEl!: HTMLElement;
  private redFlagEl!: HTMLElement;
  private blueFlagEl!: HTMLElement;
  private notifEl!: HTMLElement;
  private teamBadge!: HTMLElement;
  private roomIdEl!: HTMLElement;
  private teamArea!: HTMLElement;
  private notifTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.root = this._buildDOM();
    document.body.appendChild(this.root);
  }

  private _buildDOM(): HTMLElement {
    const root = document.createElement('div');
    root.id = 'ctf-hud';
    root.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0;
      display: flex; justify-content: center; align-items: flex-start;
      pointer-events: none; z-index: 200; font-family: 'Segoe UI', sans-serif;
    `;

    // ── Score bar ──────────────────────────────────────────────────────────
    const bar = document.createElement('div');
    bar.style.cssText = `
      display: flex; align-items: center; gap: 0;
      margin-top: 8px; border-radius: 8px; overflow: hidden;
      box-shadow: 0 2px 12px rgba(0,0,0,0.6);
    `;

    // Red score panel
    const redPanel = document.createElement('div');
    redPanel.style.cssText = `
      background: rgba(200,40,40,0.85); padding: 6px 20px;
      display: flex; flex-direction: column; align-items: center; min-width: 80px;
    `;
    const redLabel = document.createElement('span');
    redLabel.textContent = 'RED';
    redLabel.style.cssText = 'color:#ffaaaa; font-size:11px; font-weight:700; letter-spacing:2px;';
    this.redScoreEl = document.createElement('span');
    this.redScoreEl.textContent = '0';
    this.redScoreEl.style.cssText = 'color:#fff; font-size:28px; font-weight:900; line-height:1;';
    redPanel.appendChild(redLabel);
    redPanel.appendChild(this.redScoreEl);

    // Flag indicators
    const flagBar = document.createElement('div');
    flagBar.style.cssText = `
      background: rgba(0,0,0,0.75); padding: 4px 16px;
      display: flex; flex-direction: column; align-items: center; gap: 4px; min-width: 90px;
    `;
    this.timerEl = document.createElement('div');
    this.timerEl.textContent = '10:00';
    this.timerEl.style.cssText = 'color:#ddd; font-size:13px; font-weight:700; font-variant-numeric: tabular-nums;';
    const flagRow = document.createElement('div');
    flagRow.style.cssText = 'display:flex; gap:8px; align-items:center;';
    this.redFlagEl  = this._makeFlag('red');
    this.blueFlagEl = this._makeFlag('blue');
    flagRow.appendChild(this.redFlagEl);
    flagRow.appendChild(this.blueFlagEl);
    flagBar.appendChild(this.timerEl);
    flagBar.appendChild(flagRow);

    // Blue score panel
    const bluePanel = document.createElement('div');
    bluePanel.style.cssText = `
      background: rgba(40,80,200,0.85); padding: 6px 20px;
      display: flex; flex-direction: column; align-items: center; min-width: 80px;
    `;
    const blueLabel = document.createElement('span');
    blueLabel.textContent = 'BLUE';
    blueLabel.style.cssText = 'color:#aaccff; font-size:11px; font-weight:700; letter-spacing:2px;';
    this.blueScoreEl = document.createElement('span');
    this.blueScoreEl.textContent = '0';
    this.blueScoreEl.style.cssText = 'color:#fff; font-size:28px; font-weight:900; line-height:1;';
    bluePanel.appendChild(blueLabel);
    bluePanel.appendChild(this.blueScoreEl);

    bar.appendChild(redPanel);
    bar.appendChild(flagBar);
    bar.appendChild(bluePanel);
    root.appendChild(bar);

    // ── Team badge + room ID (bottom-left) ─────────────────────────────────
    this.teamArea = document.createElement('div');
    this.teamArea.style.cssText = `
      position: fixed; bottom: 12px; left: 12px;
      display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
      z-index: 200;
    `;
    this.teamBadge = document.createElement('div');
    this.teamBadge.style.cssText = `
      padding: 5px 14px; border-radius: 6px; font-size: 12px; font-weight: 900;
      letter-spacing: 2px; text-transform: uppercase; pointer-events: none;
      opacity: 0; transition: opacity 0.4s; border: 2px solid transparent;
    `;
    this.roomIdEl = document.createElement('div');
    this.roomIdEl.style.cssText = `
      padding: 3px 10px; border-radius: 4px; font-size: 11px; font-weight: 600;
      color: #999; background: rgba(0,0,0,0.5); pointer-events: auto;
      cursor: pointer; user-select: none; opacity: 0; transition: opacity 0.4s;
    `;
    this.roomIdEl.title = 'Click to copy room code';
    this.roomIdEl.addEventListener('click', () => {
      const code = this.roomIdEl.dataset.code;
      if (!code) return;
      navigator.clipboard.writeText(code).then(() => {
        const prev = this.roomIdEl.textContent;
        this.roomIdEl.textContent = 'Copied!';
        this.roomIdEl.style.color = '#aaffaa';
        setTimeout(() => {
          this.roomIdEl.textContent = prev;
          this.roomIdEl.style.color = '#999';
        }, 1500);
      });
    });
    this.teamArea.appendChild(this.teamBadge);
    this.teamArea.appendChild(this.roomIdEl);
    document.body.appendChild(this.teamArea);

    // ── Notification banner ────────────────────────────────────────────────
    this.notifEl = document.createElement('div');
    this.notifEl.style.cssText = `
      position: fixed; top: 70px; left: 50%; transform: translateX(-50%);
      background: rgba(0,0,0,0.82); color: #fff; font-size: 16px; font-weight: 700;
      padding: 8px 24px; border-radius: 6px; pointer-events: none; z-index: 210;
      opacity: 0; transition: opacity 0.25s;
      text-align: center; white-space: nowrap;
    `;
    document.body.appendChild(this.notifEl);

    return root;
  }

  private _makeFlag(team: 'red' | 'blue'): HTMLElement {
    const el = document.createElement('div');
    el.title = `${team} flag`;
    el.style.cssText = `
      width: 20px; height: 20px; border-radius: 3px; border: 2px solid rgba(255,255,255,0.4);
      background: ${team === 'red' ? '#e84040' : '#4080e8'};
      transition: filter 0.3s, opacity 0.3s;
    `;
    return el;
  }

  // ── Public API ─────────────────────────────────────────────────────────

  setRoomId(roomId: string) {
    this.roomIdEl.dataset.code = roomId;
    this.roomIdEl.textContent = `Room: ${roomId}`;
    this.roomIdEl.style.opacity = '1';
  }

  setLocalTeam(team: 'red' | 'blue') {
    const isRed = team === 'red';
    this.teamBadge.textContent = `◆ ${team.toUpperCase()} TEAM`;
    this.teamBadge.style.color           = isRed ? '#ff9090' : '#90b8ff';
    this.teamBadge.style.background      = isRed ? 'rgba(200,40,40,0.55)' : 'rgba(40,80,200,0.55)';
    this.teamBadge.style.borderColor     = isRed ? 'rgba(232,64,64,0.6)'  : 'rgba(64,128,232,0.6)';
    this.teamBadge.style.opacity         = '1';
  }

  updateScore(redScore: number, blueScore: number) {
    this.redScoreEl.textContent  = String(redScore);
    this.blueScoreEl.textContent = String(blueScore);
  }

  updateTimer(secondsLeft: number) {
    const m = Math.floor(secondsLeft / 60);
    const s = Math.floor(secondsLeft % 60);
    this.timerEl.textContent = `${m}:${s.toString().padStart(2, '0')}`;
    this.timerEl.style.color = secondsLeft <= 60 ? '#ff6666' : '#ddd';
  }

  /**
   * Update a flag icon's visual state.
   * @param status 'atBase' | 'carried' | 'dropped'
   */
  updateFlagStatus(team: 'red' | 'blue', status: 'atBase' | 'carried' | 'dropped') {
    const el = team === 'red' ? this.redFlagEl : this.blueFlagEl;
    if (status === 'atBase') {
      el.style.opacity = '1';
      el.style.filter = 'none';
      el.style.border = '2px solid rgba(255,255,255,0.4)';
    } else if (status === 'carried') {
      el.style.opacity = '1';
      el.style.filter = 'brightness(1.8) drop-shadow(0 0 4px white)';
      el.style.border = '2px solid #fff';
    } else {
      el.style.opacity = '0.5';
      el.style.filter = 'grayscale(0.6)';
      el.style.border = '2px solid rgba(255,255,255,0.2)';
    }
  }

  showNotification(msg: string, color = '#ffffff', durationMs = 3000) {
    this.notifEl.textContent = msg;
    this.notifEl.style.color = color;
    this.notifEl.style.opacity = '1';
    if (this.notifTimeout) clearTimeout(this.notifTimeout);
    this.notifTimeout = setTimeout(() => {
      this.notifEl.style.opacity = '0';
    }, durationMs);
  }

  showMatchEnd(winner: string, redScore: number, blueScore: number, returnTarget = 'lobby'): Promise<void> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = `
        position: fixed; inset: 0; background: rgba(0,0,0,0.78);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        z-index: 300; font-family: 'Segoe UI', sans-serif; color: #fff;
      `;
      const title = document.createElement('div');
      title.textContent = winner === 'draw' ? 'DRAW!' : `${winner.toUpperCase()} WINS!`;
      title.style.cssText = `
        font-size: 56px; font-weight: 900; letter-spacing: 4px;
        color: ${winner === 'red' ? '#e84040' : winner === 'blue' ? '#4080e8' : '#ddd'};
        text-shadow: 0 0 30px currentColor;
      `;
      const scores = document.createElement('div');
      scores.textContent = `${redScore} — ${blueScore}`;
      scores.style.cssText = 'font-size: 32px; font-weight: 700; margin-top: 16px; color: #ccc;';
      const sub = document.createElement('div');
      sub.style.cssText = 'font-size: 16px; color: #888; margin-top: 24px;';
      overlay.appendChild(title);
      overlay.appendChild(scores);
      overlay.appendChild(sub);
      document.body.appendChild(overlay);

      let secs = 6;
      const tick = () => {
        sub.textContent = `Returning to ${returnTarget} in ${secs}…`;
        if (secs > 0) { secs--; setTimeout(tick, 1000); }
        else { overlay.remove(); resolve(); }
      };
      tick();
    });
  }

  show() { this.root.style.display = 'flex'; }
  hide() { this.root.style.display = 'none'; }

  destroy() {
    this.root.remove();
    this.notifEl.remove();
    this.teamArea.remove();
  }
}
